package org.example.project.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import org.example.project.client.api.ActivityApiClient
import org.example.project.client.config.ApiConfig
import org.example.project.client.session.SessionRepository
import org.example.project.ui.components.EditorBanner
import org.example.project.ui.components.EditorHeader
import org.example.project.ui.components.EditorSidebar
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.lifecycle.viewmodel.initializer
import androidx.lifecycle.viewmodel.viewModelFactory
import org.example.project.ui.components.LoadingStateView
import org.example.project.ui.screens.AccessDeniedScreen
import org.example.project.ui.screens.ActivityFormScreen
import org.example.project.ui.screens.ActivityListScreen
import org.example.project.ui.screens.LoginScreen
import org.example.project.ui.theme.EditorColors
import org.example.project.ui.theme.PlayfulStudioTheme
import org.example.project.ui.viewmodel.ActivityFormViewModel
import org.example.project.ui.viewmodel.ActivityListViewModel
import org.example.project.ui.viewmodel.EditorShellViewModel
import org.example.project.ui.viewmodel.ProvideViewModelStoreOwner
import org.example.project.ui.viewmodel.activityFormViewModelKey
import org.example.project.ui.viewmodel.rememberViewModelStoreOwner
import org.example.project.planner.PlannerRepository
import org.example.project.planner.PlannerScreen
import org.example.project.planner.PlannerUiState
import org.example.project.planner.PlannerViewModel
import org.example.project.client.session.SessionState
import org.example.project.WebPlatformClient

@Composable
fun EditorApp(
    apiConfig: ApiConfig,
    sessionRepository: SessionRepository,
    activityApiClient: ActivityApiClient,
    plannerRepository: PlannerRepository = WebPlatformClient.createPlannerRepository(sessionRepository, apiConfig)
) {
    ProvideViewModelStoreOwner {
        val shellViewModel: EditorShellViewModel = viewModel(
            key = "EditorShellViewModel",
            factory = viewModelFactory {
                initializer {
                    EditorShellViewModel(sessionRepository)
                }
            }
        )

        val listViewModel: ActivityListViewModel = viewModel(
            key = "ActivityListViewModel",
            factory = viewModelFactory {
                initializer {
                    ActivityListViewModel(
                        sessionRepository = sessionRepository,
                        activityApiClient = activityApiClient,
                        onStatusMessage = shellViewModel::showStatus,
                        onErrorMessage = shellViewModel::showError
                    )
                }
            }
        )

        val plannerViewModel: PlannerViewModel = viewModel(
            key = "PlannerViewModel",
            factory = viewModelFactory {
                initializer {
                    PlannerViewModel(plannerRepository, sessionRepository, activityApiClient)
                }
            }
        )


        EditorApp(
            shellViewModel = shellViewModel,
            listViewModel = listViewModel,
            plannerViewModel = plannerViewModel,
            sessionRepository = sessionRepository,
            activityApiClient = activityApiClient
        )
    }
}

@Composable
fun EditorApp(
    shellViewModel: EditorShellViewModel,
    listViewModel: ActivityListViewModel,
    plannerViewModel: PlannerViewModel,
    sessionRepository: SessionRepository,
    activityApiClient: ActivityApiClient,
    modifier: Modifier = Modifier
) {
    ProvideViewModelStoreOwner {
        PlayfulStudioTheme {
            Surface(
                modifier = modifier.fillMaxSize(),
                color = EditorColors.Background
            ) {
            val shellState by shellViewModel.uiState.collectAsState()
            val listState by listViewModel.uiState.collectAsState()
            val plannerState by plannerViewModel.uiState.collectAsState()

            // Automatic session restoration on initial mount
            LaunchedEffect(Unit) {
                shellViewModel.restoreSession()
            }

            // Reload catalog list or planner when navigating
            LaunchedEffect(shellState.navState) {
                if (shellState.navState is EditorNavigationState.ActivityList) {
                    listViewModel.loadData()
                } else if (shellState.navState is EditorNavigationState.Planner) {
                    plannerViewModel.loadEvents()
                    plannerViewModel.loadFavorites()
                }
            }

            BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
                val isDesktop = maxWidth >= 860.dp

                when (val current = shellState.navState) {
                    is EditorNavigationState.Loading -> {
                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                .background(EditorColors.Background),
                             contentAlignment = Alignment.Center
                        ) {
                            LoadingStateView(
                                message = "Wczytywanie sesji redakcyjnej...",
                                modifier = Modifier.padding(24.dp)
                            )
                        }
                    }

                    is EditorNavigationState.Login -> {
                        Column(
                            modifier = Modifier
                                .fillMaxSize()
                                .verticalScroll(rememberScrollState())
                                .background(EditorColors.Background),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center
                        ) {
                            shellState.errorMessage?.let { err ->
                                EditorBanner(text = err, isError = true, onDismiss = shellViewModel::dismissError)
                            }
                            shellState.statusMessage?.let { msg ->
                                EditorBanner(text = msg, isError = false, onDismiss = shellViewModel::dismissStatus)
                            }

                            LoginScreen(
                                state = shellState.loginState,
                                onEmailChange = shellViewModel::onLoginEmailChange,
                                onPasswordChange = shellViewModel::onLoginPasswordChange,
                                onSubmit = shellViewModel::login
                            )
                        }
                    }

                    is EditorNavigationState.AccessDenied -> {
                        Column(
                            modifier = Modifier
                                .fillMaxSize()
                                .background(EditorColors.Background),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center
                        ) {
                            AccessDeniedScreen(
                                user = current.user,
                                onLogout = shellViewModel::logout,
                                onNavigateToPlanner = shellViewModel::navigateToPlanner
                            )
                        }
                    }

                    is EditorNavigationState.ActivityList, is EditorNavigationState.ActivityForm -> {
                        val sectionTitle = when (current) {
                            is EditorNavigationState.ActivityList -> "Katalog zabaw"
                            is EditorNavigationState.ActivityForm -> {
                                if (current.editingActivity != null) "Edycja zabawy" else "Nowa zabawa"
                            }
                        }

                        if (isDesktop) {
                            // Desktop 2-column layout: Sidebar + Main view
                            Row(modifier = Modifier.fillMaxSize()) {
                                EditorSidebar(
                                    navState = current,
                                    onNavigateToList = shellViewModel::navigateToList,
                                    onNavigateToNew = shellViewModel::navigateToNew,
                                    onNavigateToPlanner = shellViewModel::navigateToPlanner
                                )

                                Column(
                                    modifier = Modifier
                                        .weight(1f)
                                        .fillMaxHeight()
                                ) {
                                    EditorHeader(
                                        sessionState = shellState.sessionState,
                                        sectionTitle = sectionTitle,
                                        onLogout = shellViewModel::logout,
                                        isMobile = false
                                    )

                                    shellState.statusMessage?.let { msg ->
                                        EditorBanner(text = msg, isError = false, onDismiss = shellViewModel::dismissStatus)
                                    }
                                    shellState.errorMessage?.let { err ->
                                        EditorBanner(text = err, isError = true, onDismiss = shellViewModel::dismissError)
                                    }

                                    Box(
                                        modifier = Modifier
                                            .fillMaxSize()
                                            .padding(24.dp),
                                        contentAlignment = Alignment.TopCenter
                                    ) {
                                        EditorContentArea(
                                            navState = current,
                                            listState = listState,
                                            listViewModel = listViewModel,
                                            shellViewModel = shellViewModel,
                                            sessionRepository = sessionRepository,
                                            activityApiClient = activityApiClient
                                        )
                                    }
                                }
                            }
                        } else {
                            // Mobile 1-column layout: Top bar + Collapsible nav + Main view
                            Column(modifier = Modifier.fillMaxSize()) {
                                EditorHeader(
                                    sessionState = shellState.sessionState,
                                    sectionTitle = sectionTitle,
                                    onLogout = shellViewModel::logout,
                                    isMobile = true,
                                    isMobileMenuOpen = shellState.isMobileMenuOpen,
                                    onToggleMobileMenu = shellViewModel::toggleMobileMenu
                                )

                                if (shellState.isMobileMenuOpen) {
                                    EditorSidebar(
                                        navState = current,
                                        onNavigateToList = shellViewModel::navigateToList,
                                        onNavigateToNew = shellViewModel::navigateToNew,
                                        onNavigateToPlanner = shellViewModel::navigateToPlanner,
                                        isMobileDrawer = true,
                                        onCloseDrawer = shellViewModel::closeMobileDrawer
                                    )
                                }

                                shellState.statusMessage?.let { msg ->
                                    EditorBanner(text = msg, isError = false, onDismiss = shellViewModel::dismissStatus)
                                }
                                shellState.errorMessage?.let { err ->
                                    EditorBanner(text = err, isError = true, onDismiss = shellViewModel::dismissError)
                                }

                                Box(
                                    modifier = Modifier
                                        .fillMaxSize()
                                        .padding(16.dp),
                                    contentAlignment = Alignment.TopCenter
                                ) {
                                    EditorContentArea(
                                        navState = current,
                                        listState = listState,
                                        listViewModel = listViewModel,
                                        shellViewModel = shellViewModel,
                                        sessionRepository = sessionRepository,
                                        activityApiClient = activityApiClient
                                    )
                                }
                            }
                        }
                    }

                    is EditorNavigationState.Planner -> {
                        val isAdmin = (shellState.sessionState as? SessionState.AuthenticatedWeb)?.user?.roles?.contains("admin") == true
                        if (isAdmin) {
                            if (isDesktop) {
                                Row(modifier = Modifier.fillMaxSize()) {
                                    EditorSidebar(
                                        navState = current,
                                        onNavigateToList = shellViewModel::navigateToList,
                                        onNavigateToNew = shellViewModel::navigateToNew,
                                        onNavigateToPlanner = shellViewModel::navigateToPlanner
                                    )

                                    Column(
                                        modifier = Modifier
                                            .weight(1f)
                                            .fillMaxHeight()
                                    ) {
                                        EditorHeader(
                                            sessionState = shellState.sessionState,
                                            appTitle = "ApkaAnimacyjna",
                                            sectionTitle = "Planer eventów",
                                            onLogout = shellViewModel::logout,
                                            isMobile = false
                                        )

                                        shellState.statusMessage?.let { msg ->
                                            EditorBanner(text = msg, isError = false, onDismiss = shellViewModel::dismissStatus)
                                        }
                                        shellState.errorMessage?.let { err ->
                                            EditorBanner(text = err, isError = true, onDismiss = shellViewModel::dismissError)
                                        }

                                        Box(
                                            modifier = Modifier
                                                .fillMaxSize()
                                                .padding(24.dp),
                                            contentAlignment = Alignment.TopCenter
                                        ) {
                                            PlannerScreenContent(plannerState, plannerViewModel, shellViewModel)
                                        }
                                    }
                                }
                            } else {
                                Column(modifier = Modifier.fillMaxSize()) {
                                    EditorHeader(
                                        sessionState = shellState.sessionState,
                                        appTitle = "ApkaAnimacyjna",
                                        sectionTitle = "Planer eventów",
                                        onLogout = shellViewModel::logout,
                                        isMobile = true,
                                        isMobileMenuOpen = shellState.isMobileMenuOpen,
                                        onToggleMobileMenu = shellViewModel::toggleMobileMenu
                                    )

                                    if (shellState.isMobileMenuOpen) {
                                        EditorSidebar(
                                            navState = current,
                                            onNavigateToList = shellViewModel::navigateToList,
                                            onNavigateToNew = shellViewModel::navigateToNew,
                                            onNavigateToPlanner = shellViewModel::navigateToPlanner,
                                            isMobileDrawer = true,
                                            onCloseDrawer = shellViewModel::closeMobileDrawer
                                        )
                                    }

                                    shellState.statusMessage?.let { msg ->
                                        EditorBanner(text = msg, isError = false, onDismiss = shellViewModel::dismissStatus)
                                    }
                                    shellState.errorMessage?.let { err ->
                                        EditorBanner(text = err, isError = true, onDismiss = shellViewModel::dismissError)
                                    }

                                    Box(
                                        modifier = Modifier
                                            .fillMaxSize()
                                            .padding(16.dp),
                                        contentAlignment = Alignment.TopCenter
                                    ) {
                                        PlannerScreenContent(plannerState, plannerViewModel, shellViewModel)
                                    }
                                }
                            }
                        } else {
                            // Regular non-admin users: clean planner view without editorial sidebar
                            Column(modifier = Modifier.fillMaxSize()) {
                                EditorHeader(
                                    sessionState = shellState.sessionState,
                                    appTitle = "ApkaAnimacyjna",
                                    sectionTitle = "Planer eventów",
                                    onLogout = shellViewModel::logout,
                                    isMobile = !isDesktop
                                )

                                shellState.statusMessage?.let { msg ->
                                    EditorBanner(text = msg, isError = false, onDismiss = shellViewModel::dismissStatus)
                                }
                                shellState.errorMessage?.let { err ->
                                    EditorBanner(text = err, isError = true, onDismiss = shellViewModel::dismissError)
                                }

                                Box(
                                    modifier = Modifier
                                        .fillMaxSize()
                                        .padding(if (isDesktop) 24.dp else 16.dp),
                                    contentAlignment = Alignment.TopCenter
                                ) {
                                    PlannerScreenContent(plannerState, plannerViewModel, shellViewModel)
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    }
}

@Composable
private fun EditorContentArea(
    navState: EditorNavigationState,
    listState: org.example.project.ui.viewmodel.ActivityListUiState,
    listViewModel: ActivityListViewModel,
    shellViewModel: EditorShellViewModel,
    sessionRepository: SessionRepository,
    activityApiClient: ActivityApiClient
) {
    when (navState) {
        is EditorNavigationState.ActivityList -> {
            ActivityListScreen(
                state = listState,
                onSearchQueryChange = listViewModel::onSearchQueryChange,
                onSearchSubmit = listViewModel::submitSearch,
                onClearSearch = listViewModel::clearSearch,
                onStatusFilterSelected = listViewModel::onStatusFilterSelected,
                onPreviousPage = listViewModel::onPreviousPage,
                onNextPage = listViewModel::onNextPage,
                onAddNew = shellViewModel::navigateToNew,
                onEdit = shellViewModel::navigateToEdit,
                onRequestPublish = listViewModel::requestPublish,
                onDismissPublishDialog = listViewModel::dismissPublishDialog,
                onConfirmPublish = listViewModel::confirmPublish,
                onReauthenticate = shellViewModel::navigateToLogin
            )
        }

        is EditorNavigationState.ActivityForm -> {
            val formKey = activityFormViewModelKey(navState.editingActivity)
            val formOwner = rememberViewModelStoreOwner(key = formKey)

            ProvideViewModelStoreOwner(owner = formOwner) {
                val formViewModel: ActivityFormViewModel = viewModel(
                    viewModelStoreOwner = formOwner,
                    key = formKey,
                    factory = viewModelFactory {
                        initializer {
                            ActivityFormViewModel(
                                activityToEdit = navState.editingActivity,
                                sessionRepository = sessionRepository,
                                activityApiClient = activityApiClient
                            )
                        }
                    }
                )
                val formState by formViewModel.uiState.collectAsState()

                ActivityFormScreen(
                    state = formState,
                    onNameChange = formViewModel::onNameChange,
                    onDescriptionChange = formViewModel::onDescriptionChange,
                    onCategoryChange = formViewModel::onCategoryChange,
                    onMinAgeChange = formViewModel::onMinAgeChange,
                    onMaxAgeChange = formViewModel::onMaxAgeChange,
                    onDurationChange = formViewModel::onDurationChange,
                    onVideoUrlChange = formViewModel::onVideoUrlChange,
                    onCancel = shellViewModel::navigateToList,
                    onSubmit = {
                        formViewModel.save(
                            onSuccess = { savedActivity ->
                                shellViewModel.navigateToList()
                                val action = if (navState.editingActivity == null) "utworzono jako szkic" else "zaktualizowano"
                                shellViewModel.showStatus("Pomyślnie $action zabawę: \"${savedActivity.name}\"")
                                listViewModel.loadData()
                            },
                            onError = shellViewModel::showError,
                            onReauthenticate = shellViewModel::navigateToLogin
                        )
                    }
                )
            }
        }

        else -> {}
    }
}

@Composable
private fun PlannerScreenContent(
    plannerState: PlannerUiState,
    plannerViewModel: PlannerViewModel,
    shellViewModel: EditorShellViewModel
) {
    PlannerScreen(
        state = plannerState,
        onOpenEventList = plannerViewModel::openEventList,
        onBackFromEventList = plannerViewModel::backFromEventList,
        onStartCreateEvent = plannerViewModel::startCreateEvent,
        onOpenEventDetail = plannerViewModel::openEventDetail,
        onNameChange = plannerViewModel::onNameChange,
        onDateChange = plannerViewModel::onDateChange,
        onStartTimeChange = plannerViewModel::onStartTimeChange,
        onEndTimeChange = plannerViewModel::onEndTimeChange,
        onOpenAddBlockDialog = plannerViewModel::openAddBlockDialog,
        onOpenEditBlockDialog = plannerViewModel::openEditBlockDialog,
        onCloseBlockDialog = plannerViewModel::closeBlockDialog,
        onEditingBlockChange = plannerViewModel::onEditingBlockChange,
        onSaveBlock = plannerViewModel::onSaveBlock,
        onDeleteBlock = plannerViewModel::onDeleteBlock,
        onOpenFavoritesPicker = plannerViewModel::openFavoritesPicker,
        onCloseFavoritesPicker = plannerViewModel::closeFavoritesPicker,
        onSelectFavoriteActivity = plannerViewModel::selectFavoriteActivity,
        onSaveEvent = plannerViewModel::saveEvent,
        onReloadFromServer = plannerViewModel::reloadCurrentEventFromServer,
        onDeleteEvent = plannerViewModel::deleteCurrentEvent,
        onShiftBlock = plannerViewModel::shiftBlock,
        onResizeBlockStart = plannerViewModel::resizeBlockStart,
        onResizeBlockEnd = plannerViewModel::resizeBlockEnd,
        onOpenStartTimePicker = { plannerViewModel.openTimePicker(org.example.project.planner.TimePickerTarget.EVENT_START) },
        onOpenEndTimePicker = { plannerViewModel.openTimePicker(org.example.project.planner.TimePickerTarget.EVENT_END) },
        onCloseTimePicker = plannerViewModel::closeTimePicker,
        onSelectTimeFromPicker = plannerViewModel::selectTimeFromPicker,
        onOpenAddBlockTypePicker = { plannerViewModel.openAddBlockTypePicker() },
        onCloseAddBlockTypePicker = plannerViewModel::closeAddBlockTypePicker,
        onAddBlockAt = plannerViewModel::openAddBlockTypePicker,
        onOpenCustomGameEditor = { plannerViewModel.openCustomGameEditor() },
        onAddQuickOtherBlock = plannerViewModel::addQuickOtherBlock,
        onOpenCatalogPicker = { plannerViewModel.openCatalogPicker() },
        onCloseCatalogPicker = plannerViewModel::closeCatalogPicker,
        onSearchCatalog = plannerViewModel::loadCatalogActivities,
        onSelectCatalogActivity = plannerViewModel::selectCatalogActivity,
        modifier = Modifier.fillMaxSize()
    )
}


